import { DatePipe } from '@angular/common';
import {
	Component,
	computed,
	DestroyRef,
	inject,
	input,
	signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ClockService } from '@context/frontend/common';
import { isExpired } from '@context/shared/types/common';
import { TranslocoModule } from '@jsverse/transloco';
import { TuiHint, TuiSizeS, TuiSizeXL } from '@taiga-ui/core';
import { TuiBadge } from '@taiga-ui/kit';
import { combineLatest, map, takeWhile } from 'rxjs';

@Component({
	imports: [TranslocoModule, TuiBadge, DatePipe, TuiHint],
	selector: 'ctx-chemical-rei-badge',
	templateUrl: 'chemical-rei-badge.component.html',
	styleUrl: 'chemical-rei-badge.component.scss',
	host: {
		class: 'inline-block',
		'[attr.responsive]': 'responsive()',
	},
})
export class ChemicalReiBadgeComponent {
	private readonly clock = inject(ClockService);
	private readonly destroyRef = inject(DestroyRef);

	readonly reiDate = input.required<Date | null>();
	readonly size = input<TuiSizeXL | TuiSizeS>('l');
	readonly responsive = input(true);

	/** Updated every second from ClockService so badge color flips when REI time elapses. */
	readonly hasElapsed = signal(false);

	readonly appearance = computed(() =>
		this.hasElapsed() ? 'positive' : 'negative',
	);

	readonly icon = computed(() =>
		this.hasElapsed() ? '@tui.check-circle' : '@tui.alert-circle',
	);

	constructor() {
		combineLatest([toObservable(this.reiDate), this.clock.tick$])
			.pipe(
				map(([reiDate]) => reiDate != null && isExpired(reiDate)),
				takeWhile((elapsed) => !elapsed, true), // stop once elapsed (inclusive: emit the true then complete)
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((elapsed) => this.hasElapsed.set(elapsed));
	}
}
